/* eslint-disable max-classes-per-file */

import * as React from 'react';
import { Box } from '@apia/theme';
import { AxiosResponse } from 'axios';
import {
  arrayOrArray,
  EventEmitter,
  focus,
  focusSelector,
  StatefulEmitter,
  TApiaAction,
  TApiaFormElement,
  TApiaFunction,
  TApiaLoad,
  TApiaLoadText,
  TModify,
  useLatest,
} from '@apia/util';
import { notify } from '@apia/notifications';
import { Form, validationsStore } from '@apia/validations';
import { Modal, ProgressBar, TModal, useModal } from '@apia/components';
import ApiaApiContext from './ApiaApiContext';
import { IApiaApiRequestConfig } from './types';
import { ApiaActions } from './apiaApi';
import { ApiaApiFieldsContainer } from './fields/ApiaApiFieldsContainer';
import { ApiaApiButtonsContainer } from './buttons/ApiaApiButtonsContainer';

export type TMessage = {
  isHtml?: boolean;
  predicate: string;
  title?: string;
};
type TMessageLocal = { title: string; predicate: string };
type TMethod = {
  name: string;
  props: {
    attributes?: Record<string, unknown>;
    currentUrl?: string;
    funCall?: string;
    inlineArguments?: string[];
    messages?: unknown;
  };
};
const ApiaApiMessenger = new (class extends EventEmitter<{
  message: TMessageLocal;
}> {})();
const FunctionsDispatcher = new (class extends EventEmitter<{
  method: TMethod;
}> {})();

export type TApiaApiMethodHandler = {
  alert: typeof notify;
  close: () => void;
  configuration?: THandleConfiguration;
  formDefinition: TApiaLoad;
  setModalProps?: (
    configuration: THandleConfiguration,
    formDefinition?: TApiaLoad,
  ) => TModal | null;
  reset: () => void;
  setError: typeof notify;
  setMessage: (message: Record<string, unknown>) => unknown;
  setState: React.Dispatch<React.SetStateAction<IApiaApiHandlerState>>;
  state: IApiaApiHandlerState;
  setValue: (name: string, value: string) => void;
};

export type TApiaApiMethod = (
  handler: TApiaApiMethodHandler,
  props: TMethod['props'],
) => Promise<void> | void;

export type TApiaApiField = TApiaApiMethodHandler & {
  element: TModify<TApiaFormElement, { onChange: React.ChangeEventHandler }>;
};

export const apiaApiForm = 'ApiaApiForm';

type TStoredApiaApiMethod = (props?: TMethod['props']) => unknown;

const methods: Record<string, TStoredApiaApiMethod> = {};

/**
 * Este método permite cargar en forma asíncrona un método que se encuentre
 * dentro del directorio /api/methods
 */
export async function getFunction(
  name: string,
  handler: TApiaApiMethodHandler,
): Promise<TStoredApiaApiMethod | undefined> {
  return new Promise((resolve) => {
    try {
      const separateNameFromParametersRegex =
        /^\/?([\w\d_-]+)\/?(?:\(([^)]*)\))?$/;
      const match = name.match(separateNameFromParametersRegex);
      if (match) {
        const functionName = match[1];
        const parameters = match[2];
        const path: string = [
          ...(handler.configuration?.methodsPath
            ?.split('/')
            .filter((current) => !!current) ?? []),
          functionName,
        ].join('/');
        const storeMethodAndResolve = (result: {
          default: { default: TApiaApiMethod };
        }) => {
          if (typeof result.default.default !== 'function') notFound();
          const method = result.default.default;
          methods[functionName] = (props?: TMethod['props']) => {
            return method(handler, {
              ...props,
              inlineArguments:
                parameters?.split(',').map((argument) => {
                  if (argument.startsWith("'") || argument.startsWith('"'))
                    return argument.slice(1, argument.length - 1);
                  return argument;
                }) ?? [],
            });
          };
          resolve(methods[functionName]);
        };

        const notFound = () => {
          throw new Error(
            `${functionName}.ts not found at ${path}.ts nor ./${functionName}.ts`,
          );
        };

        import(
          /* webpackChunkName: "api-methods-[request]" */ `/api/methods/${path}.ts`
        )
          .then(storeMethodAndResolve)
          .catch(() => {
            if (handler.configuration?.methodsPath !== undefined)
              import(
                /* webpackChunkName: "api-methods-[request]" */ `/api/methods/${functionName}.ts`
              )

                .then(storeMethodAndResolve)
                .catch(notFound);
            else {
              notFound();
            }
          });
      }
    } catch (e) {
      console.error(e);
      handler.reset();
      handler.setError({
        type: 'danger',
        message: 'Error while loading current method.',
      });
    }
  });
}

export function isForm(
  responseObject: Record<string, unknown>,
): responseObject is TApiaLoad {
  return (
    responseObject.form !== undefined &&
    typeof responseObject.form === 'object' &&
    !!responseObject.form
  );
}

export function isFunction(
  responseObject: Record<string, unknown>,
): responseObject is TApiaLoad<TApiaFunction> {
  return (
    typeof responseObject.canClose === 'boolean' &&
    typeof responseObject.type === 'string' &&
    !!responseObject.function
  );
}

function isMessage(
  responseObject: Record<string, unknown>,
): responseObject is TApiaLoad<TApiaLoadText> {
  if (
    typeof responseObject.canClose === 'boolean' &&
    typeof responseObject.type === 'string' &&
    typeof responseObject.text === 'object' &&
    responseObject.text &&
    typeof (responseObject.text as Record<string, unknown>).label === 'string'
  ) {
    return true;
  }
  return false;
}

export interface IModalConfig {
  modalTitle?: string;
  onClose?: () => void;
  onMessage?: (message: Record<string, unknown>) => unknown;
  onMessageClose?: () => unknown;
  onSubmitted?: (
    handler: TApiaApiMethodHandler,
    response: AxiosResponse<Record<string, unknown> | null> | null,
  ) => unknown;
}

const modalConfigurationHandler = new StatefulEmitter<{
  value: THandleConfiguration;
}>();

function useModalConfiguration() {
  const value = modalConfigurationHandler.useState('value');
  const latestValue = useLatest(value);

  return latestValue;
}

export type THandleConfiguration = Partial<
  Pick<
    IApiaApiRequestConfig<unknown>,
    'modalConfiguration' | 'methodsPath' | 'setModalProps'
  >
>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
let currentForm: TApiaLoad | undefined;
const currentFormDispatcher =
  new (class currentFormDispatcher extends EventEmitter<{
    form: TApiaLoad;
  }> {
    emit<K extends 'form'>(
      eventName: K,
      params?: { form: TApiaLoad }[K] | undefined,
    ): void {
      super.emit(eventName, params as TApiaLoad);
      currentForm = params;
    }
  })();

export function handle(
  responseObject: Record<string, unknown>,
  currentUrl: string,
  configuration: THandleConfiguration,
) {
  modalConfigurationHandler.setState('value', configuration);
  if (isForm(responseObject)) {
    currentFormDispatcher.emit('form', responseObject);
    return true;
  }
  if (isFunction(responseObject)) {
    FunctionsDispatcher.emit('method', {
      name: responseObject.function.name,
      props: {
        messages: responseObject.function.messages,
        attributes: responseObject.function,
        currentUrl,
      },
    });
    return true;
  }
  if (isMessage(responseObject)) {
    ApiaApiMessenger.emit('message', {
      predicate: responseObject.text.content,
      title: responseObject.text.title,
    });
    return true;
  }
  return false;
}

export interface IApiaApiHandlerState {
  disabled: boolean;
  isLoading: boolean;
  isMultipart: boolean;
  progress: number;
  errors: Record<string, string>;
  windowIndex: number;
}

const initialState: Omit<IApiaApiHandlerState, 'windowIndex'> = {
  disabled: false,
  isLoading: false,
  isMultipart: false,
  progress: 0,
  errors: {},
};

const ApiaApiHandlerNonMemoized = () => {
  const configuration = useModalConfiguration();

  const [state, setState] = React.useState<IApiaApiHandlerState>({
    ...initialState,
    windowIndex: -1,
  });

  const [currentForm, setCurrentForm] = React.useState<TApiaLoad | undefined>(
    undefined,
  );
  React.useEffect(() => {
    return currentFormDispatcher.on('form', setCurrentForm);
  }, []);

  const setValue = React.useCallback((name: string, value: string) => {
    return validationsStore.setFieldValue(apiaApiForm, name, value);
  }, []);

  React.useEffect(() => {
    if (!currentForm) return;
    const elements = arrayOrArray(currentForm?.form?.elements?.element ?? []);

    let isMultipart = false;
    const elementsValues = Object.values(elements);
    let i = 0;
    while (i < elementsValues.length && !isMultipart) {
      if (elementsValues[i++].type === 'file') {
        isMultipart = true;
      }
    }

    setState((currentState) => {
      return {
        ...currentState,
        ...initialState,
        isMultipart,
      };
    });
  }, [currentForm]);

  const { show, onClose, ...modalHookProps } = useModal();

  const close = React.useCallback(() => {
    onClose();
    currentFormDispatcher.emit('form', undefined);
    setState((currentState) => {
      return { ...currentState, ...initialState };
    });
    configuration.current?.modalConfiguration?.onClose?.();

    validationsStore.unregisterForm(apiaApiForm);
  }, [onClose, configuration]);

  const modalRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const elements = modalRef.current?.querySelectorAll(
      'a,input,textarea,button',
    );
    if (elements?.length === 1)
      void focus.on(([...elements] as HTMLElement[])[0]);
  });

  const setError = React.useCallback(
    (notification: Parameters<typeof notify>[0]) => {
      notify({ ...notification });
    },
    [],
  );

  const initialFocusGetter = React.useCallback((ref: HTMLElement) => {
    return ref?.querySelector(focusSelector) as HTMLElement;
  }, []);

  const defaultModalProps = React.useMemo(
    (): TModal => ({
      onClose: close,
      id: apiaApiForm,
      title:
        configuration.current?.modalConfiguration?.modalTitle ??
        currentForm?.form.title,
      size: 'md-fixed',
      shouldCloseOnEsc: !state.disabled,
      shouldCloseOnOverlayClick: !state.disabled,
      initialFocusGetter,
    }),
    [
      close,
      configuration,
      currentForm?.form.title,
      initialFocusGetter,
      state.disabled,
    ],
  );

  const getModalProps = React.useCallback(
    (innerHandler: TApiaApiMethodHandler) => {
      if (innerHandler.setModalProps) {
        const overrideProps = innerHandler.setModalProps?.(
          {
            methodsPath: innerHandler.configuration?.methodsPath,
            modalConfiguration: innerHandler.configuration?.modalConfiguration,
          },
          innerHandler.formDefinition,
        );
        if (overrideProps) {
          const currentOnClose = () => {
            overrideProps?.onClose?.();
            defaultModalProps.onClose?.();
          };
          const currentOnExited = () => {
            overrideProps?.onExited?.();
            modalHookProps?.onExited?.();
          };
          return {
            ...defaultModalProps,
            ...overrideProps,
            ...modalHookProps,
            onClose: currentOnClose,
            onExited: currentOnExited,
          };
        }
      }
      return { ...defaultModalProps, ...modalHookProps };
    },
    [defaultModalProps, modalHookProps],
  );

  const getHandler = React.useCallback(() => {
    const newHandler: TApiaApiMethodHandler = {
      alert,
      close,
      configuration: configuration.current,
      formDefinition: currentForm as TApiaLoad,
      reset: () => {
        return setState((currentState) => {
          return {
            ...currentState,
            ...initialState,
          };
        });
      },
      setError,
      setMessage: (message) => {
        if (configuration.current?.modalConfiguration?.onMessage)
          configuration.current.modalConfiguration.onMessage(message);
      },
      setState,
      state,
      setValue,
    };
    return newHandler;
  }, [close, configuration, currentForm, setError, state, setValue]);

  const handler = getHandler();
  const modalProps = getModalProps(handler);

  const handleAction = React.useCallback(
    async (action: TModify<TApiaAction, { param: string[] }>) => {
      const innerHandler = getHandler();
      if (action.toDo === 'ajaxHiddeAll') {
        close();
      }
      if (action.toDo === 'functionTimedCall') {
        const method = await getFunction(
          arrayOrArray(action.param)[1],
          innerHandler,
        );

        if (method) {
          /*
           *  Aquí el timeout se utiliza debido a que Apia lo establece de esa
           *  forma,
           *  Existe un retorno de Apia llamado functionTimedCall que
           *  espera que una función se ejecute luego de un timeout, y
           *  yo solamente respeté dicho comportamiento.
           */
          setTimeout(
            () =>
              method({
                currentUrl: innerHandler.formDefinition?.form.action ?? 'noUrl',
              }),
            Number(action.param[0]),
          );
        }
      }
    },
    [close, getHandler],
  );

  const handleFunction = React.useCallback(
    async ({ name, props: { messages, attributes, currentUrl } }: TMethod) => {
      const innerHandler = getHandler();
      const method = await getFunction(`${name}`, innerHandler);
      if (method) {
        method({ messages, attributes, currentUrl });
      }
    },
    [getHandler],
  );

  const handleMessage = React.useCallback(
    (ev: TMessage) => {
      const innerHandler = getHandler();
      notify({
        message: ev.predicate,
        type: 'warning',
        onClose: innerHandler.configuration?.modalConfiguration?.onMessageClose,
      });
      const onMessage =
        innerHandler.configuration?.modalConfiguration?.onMessage;
      if (onMessage) {
        onMessage(ev);
      }
    },
    [getHandler],
  );

  React.useEffect(() => {
    const u1 = ApiaApiMessenger.on('message', handleMessage);
    const u2 = FunctionsDispatcher.on('method', handleFunction);
    const u3 = ApiaActions.on('action', handleAction);

    return () => {
      u1();
      u2();
      u3();
    };
  }, [handleAction, handleFunction, handleMessage, state.windowIndex]);

  React.useEffect(() => {
    if (currentForm) {
      show();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentForm]);

  const formRef = React.useCallback(
    async (el: HTMLElement) => {
      if (el && currentForm?.form.onLoad) {
        const method = await getFunction(
          `${currentForm?.form.onLoad}`,
          getHandler(),
        );
        if (method) {
          method();
        }
      }
    },
    [currentForm?.form.onLoad, getHandler],
  );

  return (
    <ApiaApiContext id={apiaApiForm}>
      <Modal
        {...modalProps}
        ref={modalRef}
        variant="layout.common.modals.apiaApi"
      >
        {currentForm && (
          <Form
            name={apiaApiForm}
            unregisterOnUnmount
            avoidFieldsOverride
            className="handler__form"
          >
            <ApiaApiFieldsContainer definition={currentForm} {...handler} />
            {state.isMultipart && state.progress > 0 && (
              <Box className="progressBox">
                <ProgressBar
                  id="ApiaApiHandler progress"
                  progress={state.progress}
                  loading
                />
              </Box>
            )}
            <ApiaApiButtonsContainer definition={currentForm} {...handler} />
            <Box ref={formRef} sx={{ display: 'none' }} />
          </Form>
        )}
      </Modal>
    </ApiaApiContext>
  );
};

export const ApiaApiHandler = React.memo(ApiaApiHandlerNonMemoized);
